import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from './AuthContext';
import { fetchNotifications, getUnreadCount, markAsRead as markAsReadService, markAllAsRead as markAllAsReadService } from '../services/notificationService';
import { isPushSupported, getPushPermission, shouldPromptForPush, tryEnablePush } from '../services/pushNotificationService';

const NotificationContext = createContext({});

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const channelRef = useRef(null);

  // Load initial notifications
  const loadNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const { data, error } = await fetchNotifications({ limit: 30 });
      if (!error && data) {
        setNotifications(data);
      }
      const { count } = await getUnreadCount();
      setUnreadCount(count || 0);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Subscribe to realtime notifications
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    loadNotifications();

    // Subscribe to new notifications for this user
    const channel = supabase
      .channel('user-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const newNotification = payload.new;
          setNotifications(prev => [newNotification, ...prev]);
          setUnreadCount(prev => prev + 1);

          // #434: a fresh notification while the tab is open is exactly the
          // "meaningful event" pushNotificationService.js was written to be
          // called after (D13, cautious opt-in) — but nothing ever called it,
          // so push never actually delivered. Wire it here: if permission was
          // already granted, self-heal a missing subscription; otherwise only
          // ask if we're not inside the 30-day dismissal cooldown.
          if (isPushSupported()) {
            const gate = getPushPermission() === 'granted'
              ? Promise.resolve(true)
              : shouldPromptForPush();
            gate.then((should) => { if (should) tryEnablePush(); }).catch(() => {});
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [isAuthenticated, user?.id, loadNotifications]);

  const markAsRead = async (ids) => {
    const { error } = await markAsReadService(ids);
    if (!error) {
      setNotifications(prev =>
        prev.map(n => ids.includes(n.id) ? { ...n, read_at: new Date().toISOString() } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - ids.length));
    }
  };

  const markAllRead = async () => {
    const { error } = await markAllAsReadService();
    if (!error) {
      setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnreadCount(0);
    }
  };

  const value = {
    notifications,
    unreadCount,
    loading,
    loadNotifications,
    markAsRead,
    markAllRead,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export default NotificationContext;
